import { z } from 'zod';
import {
  ContestLeaderSchema,
  LeaderboardMoveSchema,
  LeaderboardRowSchema,
  LeaderboardViewSchema,
  MatchViewSchema,
} from './leaderboard';
import { ScoringAuditFeedEntrySchema } from './scoring';

/**
 * SSE wire protocol (text/event-stream). Each message:
 *   id: <version>            (monotonic per tournament; browsers resend it as Last-Event-ID)
 *   event: <type>
 *   data: <JSON of the event below>
 * On connect (or when Last-Event-ID is too old / unknown) the server first sends `snapshot`.
 * After that only `patch` (+ other deltas). A `heartbeat` comment line is sent every 15 s.
 *
 * Version rules (CLAUDE.md §6):
 *  - `version` (= SSE id) is one tournament-wide sequence shared by ALL events and audiences, so a
 *    client must NOT compare it with patch.baseVersion. Use it only for Last-Event-ID resume.
 *  - leaderboard.patch.baseVersion = the version of the previous leaderboard.* event of the SAME
 *    viewKey (each viewKey is audience-specific: `pub:` / `adm:`). Clients keep
 *    lastLbVersion[viewKey]; refetch the snapshot only when baseVersion != lastLbVersion[viewKey].
 */
export const RealtimeEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('leaderboard.snapshot'),
    version: z.number(),
    viewKey: z.string(),
    view: LeaderboardViewSchema,
  }),
  z.object({
    type: z.literal('leaderboard.patch'),
    version: z.number(),
    /** version of the previous leaderboard.* event for this viewKey (not the previous SSE id) */
    baseVersion: z.number(),
    viewKey: z.string(),
    /** new rows and rows whose content (score, thru, status...) changed: full row */
    rows: z.array(LeaderboardRowSchema),
    /**
     * rows whose ONLY change is position/positionLabel/tied/rank/previousPosition: compact entry
     * (optional for older producers; absent = none)
     */
    moves: z.array(LeaderboardMoveSchema).optional(),
    /**
     * full order of competitorIds after this patch, or EMPTY = re-sort the merged rows by `rank`
     * (unique 1..n final order). The server sends [] whenever ranks reproduce the order.
     */
    order: z.array(z.string()),
    removed: z.array(z.string()),
  }),
  z.object({
    type: z.literal('score.updated'),
    version: z.number(),
    roundId: z.uuid(),
    competitorId: z.string(),
    holeNumber: z.number(),
    strokes: z.number().nullable(),
    putts: z.number().nullable(),
    pickedUp: z.boolean(),
    /** ADMIN = admin correction -> caddy device shows "!" and the cell becomes ADMIN_LOCKED */
    source: z.enum(['CADDY', 'ADMIN', 'SELF', 'IMPORT']),
    scoreVersion: z.number(),
  }),
  z.object({
    type: z.literal('contest.updated'),
    version: z.number(),
    contest: ContestLeaderSchema,
    /**
     * admin stream only: the VALID entry this write created (scorer or admin record), so the
     * console can announce it; absent on public / caddy streams and for void / restore writes
     */
    entry: z
      .object({
        entryId: z.uuid(),
        participantId: z.uuid(),
        name: z.string(),
        bib: z.number(),
        distanceMeters: z.number(),
      })
      .optional(),
  }),
  z.object({ type: z.literal('match.updated'), version: z.number(), match: MatchViewSchema }),
  z.object({
    type: z.literal('round.status'),
    version: z.number(),
    roundId: z.uuid(),
    roundNo: z.number(),
    status: z.enum(['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'LOCKED']),
  }),
  z.object({
    type: z.literal('tournament.status'),
    version: z.number(),
    status: z.string(),
  }),
  /** admin + caddy (groupIds) streams: sign-off created / invalidated */
  z.object({
    type: z.literal('signoff.updated'),
    version: z.number(),
    roundId: z.uuid(),
    participantId: z.uuid(),
    status: z.enum(['SIGNED', 'DISPUTED', 'INVALIDATED']),
    method: z.enum(['PLAYER_PORTAL', 'CADDY_DEVICE', 'ADMIN']),
  }),
  /** admin stream only */
  z.object({
    type: z.literal('audit.appended'),
    version: z.number(),
    auditId: z.uuid(),
    action: z.string(),
    roundId: z.uuid(),
    competitorId: z.string().nullable(),
    holeNumber: z.number(),
    /**
     * the appended row as GET /tournaments/:tid/audit returns it minus userAgent / deviceInfo
     * (optional, additive): consoles prepend it to their live feed without a refetch; absent =
     * refetch the audit queries
     */
    entry: ScoringAuditFeedEntrySchema.optional(),
  }),
  /** admin stream only: auto-detected special-award winners were added, removed or changed */
  z.object({
    type: z.literal('awards.updated'),
    version: z.number(),
  }),
  /**
   * admin stream only (§15 #478): the award plan / finalize / publish state changed on another
   * device — award builders re-read GET /award-plan (planVersion = AwardPlanResponse.version)
   */
  z.object({
    type: z.literal('awards.plan.updated'),
    version: z.number(),
    planVersion: z.number(),
  }),
  /** public + admin (§15 #478): the published award sheet changed (publish, finalize) — refetch */
  z.object({
    type: z.literal('awards.sheet.updated'),
    version: z.number(),
    published: z.boolean(),
  }),
  /**
   * public + admin (§15 #478): the ceremony presenter moved to slide `index` of
   * `ceremonySlides(sheet)`; active false = ceremony ended (screens leave ceremony mode)
   */
  z.object({
    type: z.literal('awards.ceremony'),
    version: z.number(),
    active: z.boolean(),
    index: z.number().int().min(0),
  }),
  /** admin stream only */
  z.object({
    type: z.literal('anomaly.raised'),
    version: z.number(),
    anomalyId: z.uuid(),
    kind: z.string(),
    severity: z.string(),
  }),
]);
export type RealtimeEvent = z.infer<typeof RealtimeEventSchema>;
export type RealtimeEventType = RealtimeEvent['type'];

/** Which audience receives an event. Publishers choose; the SSE layer filters per connection. */
export type RealtimeAudience = 'public' | 'admin' | 'caddy';

/**
 * Redis pub/sub envelope (api instance <-> api instance).
 * Publish leaderboard.* events per audience (public views carry redacted rows): one envelope with
 * audience ['public'] and viewKey 'pub:...', another with ['admin'] and 'adm:...'. Never send one
 * leaderboard envelope to several audiences.
 */
export interface RealtimeEnvelope {
  tournamentId: string;
  audience: RealtimeAudience[];
  /** caddy-scoped events: only deliver to caddy sessions of these group ids (empty = all caddies) */
  groupIds?: string[];
  /** leaderboard.* events: only deliver to connections subscribed to this viewKey */
  viewKey?: string;
  event: RealtimeEvent;
}

/** Hub control messages (not logged, not sent to browsers). */
export interface RealtimeControlMessage {
  tournamentId: string;
  /**
   * link.revoked: close local streams opened with this (now rotated) link token.
   * caddy.regroup: close caddy-audience streams (their groupIds are fixed at connect) so the device
   * reconnects with Last-Event-ID and re-reads its context; caddyIds null = every scorer stream of
   * the tournament (caddies and SELF_ENTRY players).
   * sessions.revoked: close every local stream opened with one of these sessions (logout, password
   * change / reset, user disable, caddy deactivate / PIN reset, tenant suspension); published with
   * tournamentId '*' on the hub's sessions channel because a session spans tournaments.
   * public.closed: publicLeaderboard was turned off — close the tournament's public-audience
   * streams (spectators / TV) and drop cached public links; reconnects get 403 LEADERBOARD_HIDDEN.
   */
  control:
    | { type: 'link.revoked'; linkToken: string }
    | { type: 'caddy.regroup'; caddyIds: string[] | null }
    | { type: 'sessions.revoked'; sessionIds: string[] }
    | { type: 'public.closed' };
}
